"use client";

/* istanbul ignore file */
import { c as _c } from "react/compiler-runtime";
import React, { useContext, useRef, useState, type JSX } from "react";
import CreateWaveDrops from "./drops/CreateWaveDrops";
import CreateWavesMainSteps from "./main-steps/CreateWavesMainSteps";
import CreateWaveOverview from "./overview/CreateWaveOverview";
import CreateWaveGroups from "./groups/CreateWaveGroups";
import CreateWaveDates from "./dates/CreateWaveDates";
import CreateWaveOutcomes from "./outcomes/CreateWaveOutcomes";
import { CreateWaveStep } from "@/types/waves.types";
import CreateWaveVoting from "./voting/CreateWaveVoting";
import CreateWaveApproval from "./approval/CreateWaveApproval";
import CreateWaveActions from "./utils/CreateWaveActions";
import CreateWaveDescription, { CreateWaveDescriptionHandles } from "./description/CreateWaveDescription";
import { getCreateNewWaveBody } from "@/helpers/waves/create-wave.helpers";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { ApiCreateWaveDropRequest } from "@/generated/models/ApiCreateWaveDropRequest";
import { useRouter } from "next/navigation";
import { generateDropPart } from "./services/waveMediaService";
import { getAdminGroupId } from "./services/waveGroupService";
import { useAddWaveMutation } from "./services/waveApiService";
import { useWaveConfig } from "./hooks/useWaveConfig";
import useCapacitor from "@/hooks/useCapacitor";
import CreateWaveFlow from "./CreateWaveFlow";
import { multiPartUpload } from "./services/multiPartUpload";
import { ApiIdentity } from "@/generated/models/ApiIdentity";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveRoute } from "@/helpers/navigation.helpers";
export default function CreateWave(t0) {
  const $ = _c(113);
  const {
    profile,
    onBack,
    onSuccess
  } = t0;
  const router = useRouter();
  const {
    isIos,
    keyboardVisible
  } = useCapacitor();
  const {
    requestAuth,
    setToast,
    connectedProfile
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops,
    onWaveCreated
  } = useContext(ReactQueryWrapperContext);
  const [submitting, setSubmitting] = useState(false);
  const {
    config,
    step,
    selectedOutcomeType,
    errors,
    groupsCache,
    setOverview,
    setDates,
    setDrops,
    setOutcomes,
    setDropsAdminCanDelete,
    onStep,
    onOutcomeTypeChange,
    onGroupSelect,
    onVotingTypeChange,
    onCategoryChange,
    onProfileIdChange,
    onTimeWeightedVotingChange,
    onThresholdChange,
    onThresholdTimeChange,
    onChatEnabledChange
  } = useWaveConfig();
  const {
    isApp
  } = useDeviceInfo();
  const createWaveDescriptionRef = useRef(null);
  let t1;
  if ($[0] !== isApp || $[1] !== onSuccess || $[2] !== onWaveCreated || $[3] !== router || $[4] !== waitAndInvalidateDrops) {
    t1 = response => {
      waitAndInvalidateDrops();
      onWaveCreated();
      onSuccess?.();
      if (isApp) {
        router.replace(getWaveRoute({
          waveId: response.id,
          isDirectMessage: false,
          isApp: true
        }));
      } else {
        router.push(`/waves?wave=${response.id}`);
      }
    };
    $[0] = isApp;
    $[1] = onSuccess;
    $[2] = onWaveCreated;
    $[3] = router;
    $[4] = waitAndInvalidateDrops;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== setToast) {
    t2 = error => {
      setToast({
        message: error as string,
        type: "error"
      });
    };
    $[6] = setToast;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setSubmitting(false);
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2) {
    t4 = {
      onSuccess: t1,
      onError: t2,
      onSettled: t3
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const addWaveMutation = useAddWaveMutation(t4);
  const [showDropError, setShowDropError] = useState(false);
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = haveDrop => {
      if (haveDrop) {
        setShowDropError(false);
      }
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const onHaveDropToSubmitChange = t5;
  let t6;
  if ($[13] !== addWaveMutation || $[14] !== config || $[15] !== connectedProfile?.handle || $[16] !== connectedProfile?.primary_wallet || $[17] !== requestAuth || $[18] !== setToast) {
    t6 = async () => {
      setSubmitting(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setSubmitting(false);
        return;
      }
      const drop = createWaveDescriptionRef.current?.requestDrop() ?? null;
      if (!drop?.parts.length) {
        setSubmitting(false);
        setShowDropError(true);
        return;
      }
      const adminGroupId = await getAdminGroupId({
        adminGroupId: config.groups.admin,
        primaryWallet: connectedProfile?.primary_wallet,
        handle: connectedProfile?.handle ?? undefined,
        onError: error_0 => {
          setToast({
            message: typeof error_0 === "string" ? error_0 : "Failed to get admin group",
            type: "error"
          });
        }
      });
      if (!adminGroupId) {
        setSubmitting(false);
        return;
      }
      const dropParts = await Promise.all(drop.parts.map(_temp));
      const dropRequest = {
        title: drop.title,
        parts: dropParts.map(_temp3),
        referenced_nfts: drop.referenced_nfts.map(_temp4),
        mentioned_users: drop.mentioned_users.map(_temp5),
        metadata: drop.metadata.map(_temp6),
        signature: null
      };
      const picture = config.overview.image ? await multiPartUpload({
        file: config.overview.image,
        path: "wave"
      }) : null;
      const waveBody = getCreateNewWaveBody({
        config: {
          ...config,
          groups: {
            ...config.groups,
            admin: adminGroupId
          }
        },
        picture: picture?.url ?? null,
        drop: dropRequest
      });
      await addWaveMutation.mutateAsync(waveBody);
    };
    $[13] = addWaveMutation;
    $[14] = config;
    $[15] = connectedProfile?.handle;
    $[16] = connectedProfile?.primary_wallet;
    $[17] = requestAuth;
    $[18] = setToast;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const onComplete = t6;
  let t7;
  if ($[20] !== config.overview || $[21] !== errors || $[22] !== setOverview) {
    t7 = <CreateWaveOverview overview={config.overview} errors={errors} setOverview={setOverview} />;
    $[20] = config.overview;
    $[21] = errors;
    $[22] = setOverview;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== config.chat.enabled || $[25] !== config.drops.adminCanDeleteDrops || $[26] !== config.groups || $[27] !== config.overview.type || $[28] !== groupsCache || $[29] !== onChatEnabledChange || $[30] !== onGroupSelect || $[31] !== setDropsAdminCanDelete) {
    t8 = <CreateWaveGroups waveType={config.overview.type} groups={config.groups} groupsCache={groupsCache} chatEnabled={config.chat.enabled} adminCanDeleteDrops={config.drops.adminCanDeleteDrops} setChatEnabled={onChatEnabledChange} onGroupSelect={onGroupSelect} setDropsAdminCanDelete={setDropsAdminCanDelete} />;
    $[24] = config.chat.enabled;
    $[25] = config.drops.adminCanDeleteDrops;
    $[26] = config.groups;
    $[27] = config.overview.type;
    $[28] = groupsCache;
    $[29] = onChatEnabledChange;
    $[30] = onGroupSelect;
    $[31] = setDropsAdminCanDelete;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== config.dates || $[34] !== config.overview.type || $[35] !== setDates) {
    t9 = <CreateWaveDates waveType={config.overview.type} dates={config.dates} setDates={setDates} />;
    $[33] = config.dates;
    $[34] = config.overview.type;
    $[35] = setDates;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== config.drops || $[38] !== config.overview.type || $[39] !== errors || $[40] !== setDrops) {
    t10 = <CreateWaveDrops waveType={config.overview.type} drops={config.drops} errors={errors} setDrops={setDrops} />;
    $[37] = config.drops;
    $[38] = config.overview.type;
    $[39] = errors;
    $[40] = setDrops;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== config.overview.type || $[43] !== config.voting.category || $[44] !== config.voting.profileId || $[45] !== config.voting.timeWeighted || $[46] !== config.voting.type || $[47] !== errors || $[48] !== onCategoryChange || $[49] !== onProfileIdChange || $[50] !== onTimeWeightedVotingChange || $[51] !== onVotingTypeChange) {
    t11 = <CreateWaveVoting waveType={config.overview.type} selectedType={config.voting.type} category={config.voting.category} profileId={config.voting.profileId} errors={errors} onTypeChange={onVotingTypeChange} setCategory={onCategoryChange} setProfileId={onProfileIdChange} timeWeighted={config.voting.timeWeighted} onTimeWeightedChange={onTimeWeightedVotingChange} />;
    $[42] = config.overview.type;
    $[43] = config.voting.category;
    $[44] = config.voting.profileId;
    $[45] = config.voting.timeWeighted;
    $[46] = config.voting.type;
    $[47] = errors;
    $[48] = onCategoryChange;
    $[49] = onProfileIdChange;
    $[50] = onTimeWeightedVotingChange;
    $[51] = onVotingTypeChange;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== config.approval.threshold || $[54] !== config.approval.thresholdTimeMs || $[55] !== errors || $[56] !== onThresholdChange || $[57] !== onThresholdTimeChange) {
    t12 = <CreateWaveApproval threshold={config.approval.threshold} thresholdTimeMs={config.approval.thresholdTimeMs} errors={errors} setThreshold={onThresholdChange} setThresholdTimeMs={onThresholdTimeChange} />;
    $[53] = config.approval.threshold;
    $[54] = config.approval.thresholdTimeMs;
    $[55] = errors;
    $[56] = onThresholdChange;
    $[57] = onThresholdTimeChange;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  let t13;
  if ($[59] !== config.dates || $[60] !== config.outcomes || $[61] !== config.overview.type || $[62] !== errors || $[63] !== onOutcomeTypeChange || $[64] !== selectedOutcomeType || $[65] !== setOutcomes) {
    t13 = <CreateWaveOutcomes outcomes={config.outcomes} outcomeType={selectedOutcomeType} waveType={config.overview.type} errors={errors} dates={config.dates} setOutcomeType={onOutcomeTypeChange} setOutcomes={setOutcomes} />;
    $[59] = config.dates;
    $[60] = config.outcomes;
    $[61] = config.overview.type;
    $[62] = errors;
    $[63] = onOutcomeTypeChange;
    $[64] = selectedOutcomeType;
    $[65] = setOutcomes;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  let t14;
  if ($[67] !== config.overview.image) {
    t14 = config.overview.image ? URL.createObjectURL(config.overview.image) : null;
    $[67] = config.overview.image;
    $[68] = t14;
  } else {
    t14 = $[68];
  }
  let t15;
  if ($[69] !== config.overview.name || $[70] !== t14) {
    t15 = {
      name: config.overview.name,
      image: t14,
      id: null
    };
    $[69] = config.overview.name;
    $[70] = t14;
    $[71] = t15;
  } else {
    t15 = $[71];
  }
  let t16;
  if ($[72] !== profile || $[73] !== showDropError || $[74] !== t15) {
    t16 = <CreateWaveDescription ref={createWaveDescriptionRef} profile={profile} showDropError={showDropError} wave={t15} onHaveDropToSubmitChange={onHaveDropToSubmitChange} />;
    $[72] = profile;
    $[73] = showDropError;
    $[74] = t15;
    $[75] = t16;
  } else {
    t16 = $[75];
  }
  let t17;
  if ($[76] !== t10 || $[77] !== t11 || $[78] !== t12 || $[79] !== t13 || $[80] !== t16 || $[81] !== t7 || $[82] !== t8 || $[83] !== t9) {
    t17 = {
      [CreateWaveStep.OVERVIEW]: t7,
      [CreateWaveStep.GROUPS]: t8,
      [CreateWaveStep.DATES]: t9,
      [CreateWaveStep.DROPS]: t10,
      [CreateWaveStep.VOTING]: t11,
      [CreateWaveStep.APPROVAL]: t12,
      [CreateWaveStep.OUTCOMES]: t13,
      [CreateWaveStep.DESCRIPTION]: t16
    };
    $[76] = t10;
    $[77] = t11;
    $[78] = t12;
    $[79] = t13;
    $[80] = t16;
    $[81] = t7;
    $[82] = t8;
    $[83] = t9;
    $[84] = t17;
  } else {
    t17 = $[84];
  }
  const stepComponent = t17;
  const t18 = `Create Wave ${config.overview.name ? `"${config.overview.name}"` : ""}`;
  let t19;
  if ($[85] !== onStep) {
    t19 = step_0 => onStep({
      step: step_0,
      direction: "backward"
    });
    $[85] = onStep;
    $[86] = t19;
  } else {
    t19 = $[86];
  }
  let t20;
  if ($[87] !== config.overview.type || $[88] !== step || $[89] !== t19) {
    t20 = <div className="tw-1/4"><CreateWavesMainSteps activeStep={step} waveType={config.overview.type} onStep={t19} /></div>;
    $[87] = config.overview.type;
    $[88] = step;
    $[89] = t19;
    $[90] = t20;
  } else {
    t20 = $[90];
  }
  const t21 = `tw-flex-1 ${isIos && !keyboardVisible ? "tw-mb-10" : ""}`;
  const t22 = stepComponent[step];
  let t23;
  if ($[91] !== t22) {
    t23 = <div className="tw-flex-1 tw-w-full">{t22}</div>;
    $[91] = t22;
    $[92] = t23;
  } else {
    t23 = $[92];
  }
  let t24;
  if ($[93] !== config || $[94] !== onComplete || $[95] !== onStep || $[96] !== selectedOutcomeType || $[97] !== step || $[98] !== submitting) {
    t24 = !selectedOutcomeType && <div className="tw-mt-auto"><CreateWaveActions setStep={(step_1, direction) => onStep({
        step: step_1,
        direction
      })} step={step} config={config} submitting={submitting} onComplete={onComplete} /></div>;
    $[93] = config;
    $[94] = onComplete;
    $[95] = onStep;
    $[96] = selectedOutcomeType;
    $[97] = step;
    $[98] = submitting;
    $[99] = t24;
  } else {
    t24 = $[99];
  }
  let t25;
  if ($[100] !== t23 || $[101] !== t24) {
    t25 = <div className="tw-relative tw-w-full tw-bg-iron-900 tw-p-4 lg:tw-p-8 tw-rounded-xl"><div className="tw-relative tw-h-full"><div className="tw-flex tw-flex-col tw-h-full">{t23}{t24}</div></div></div>;
    $[100] = t23;
    $[101] = t24;
    $[102] = t25;
  } else {
    t25 = $[102];
  }
  let t26;
  if ($[103] !== t21 || $[104] !== t25) {
    t26 = <div className={t21}>{t25}</div>;
    $[103] = t21;
    $[104] = t25;
    $[105] = t26;
  } else {
    t26 = $[105];
  }
  let t27;
  if ($[106] !== t20 || $[107] !== t26) {
    t27 = <div className="tw-mt-4 xl:tw-max-w-[60rem] tw-mx-auto lg:tw-flex tw-gap-x-16 tw-justify-between tw-h-full tw-w-full">{t20}{t26}</div>;
    $[106] = t20;
    $[107] = t26;
    $[108] = t27;
  } else {
    t27 = $[108];
  }
  let t28;
  if ($[109] !== onBack || $[110] !== t18 || $[111] !== t27) {
    t28 = <CreateWaveFlow title={t18} onBack={onBack}>{t27}</CreateWaveFlow>;
    $[109] = onBack;
    $[110] = t18;
    $[111] = t27;
    $[112] = t28;
  } else {
    t28 = $[112];
  }
  return t28;
}
function _temp6(meta) {
  return {
    data_key: meta.data_key,
    data_value: meta.data_value
  };
}
function _temp5(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}
function _temp4(nft) {
  return {
    contract: nft.contract,
    token: nft.token,
    name: nft.name
  };
}
function _temp3(part_0) {
  return {
    content: part_0.content,
    quoted_drop: part_0.quoted_drop,
    media: part_0.media.map(_temp2)
  };
}
function _temp2(media) {
  return {
    url: media.url,
    mime_type: media.mime_type
  };
}
function _temp(part) {
  return generateDropPart(part);
}